import { Icon, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { VALIDATION_ERRORS } from '@hero/constants';
import { trimStringsInObject } from '@hero/utils';
import { isEmailValid, isNameValid } from '@hero/utils/validationUtils';

import { SpreadSheetContactTable } from '../../Table/Advance/SpreadSheetContactTable';
import { STEP } from '../constant';

import { REVIEW_IMPORT_STEP } from './constants';
import { FileUploadArea } from './FileUploadArea';
import * as S from './style';

const csvHeader = ['email', 'firstName', 'lastName', 'birthday', 'anniversary'];

const INNER_STEP = {
  upload: 'upload',
  table: 'table'
};

function UploadSpreadSheet({
  isLoading,
  setUploadedRecipients,
  setActiveScene,
  uploadedRecipients,
  setFooterProps,
  setStep
}) {
  const { t } = useTranslation();
  const [innerStep, setInnerStep] = useState(INNER_STEP.upload);
  const { addModalError, reset } = useValidationErrorsStore();

  const handleFile = async file => {
    await new Promise(resolve => {
      let message = '';
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = async ({ target: { result } }) => {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(result, { type: 'binary', cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const range = XLSX.utils.decode_range(ws['!ref']);
        // TODO: isEmailValid is not correct way, we should check like equal "email" and other columns
        range.s.r = isEmailValid(ws?.A1?.h) ? 0 : 1; // <-- if file has header index will set as 1 and skip row 0
        ws['!ref'] = XLSX.utils.encode_range(range);

        const recipients = XLSX.utils.sheet_to_json(ws, {
          header: csvHeader,
          raw: true,
          rawNumbers: false
        });

        if (recipients.length) {
          message = t('contactsSuccessfullyAdded', { count: recipients.length });
        } else {
          message = t('noContactsAreAdded');
        }

        toast(
          {
            title: message,
            message: t('addMoreContactsOrContinue')
          },
          {
            className: 'toast-wrapper success'
          }
        );

        setUploadedRecipients(oldUploadedRecipients => [
          ...oldUploadedRecipients,
          ...sortBy(
            recipients.map((recipient, key) => {
              const trimRecipient = trimStringsInObject(recipient);

              const birthday = dayjs(trimRecipient.birthday);
              const anniversary = dayjs(trimRecipient.anniversary);

              return {
                id: key,
                tags: [],
                hasError: !(
                  isEmailValid(trimRecipient.email) && isNameValid(trimRecipient.firstName)
                ),
                ...trimRecipient,
                birthday:
                  trimRecipient.birthday && birthday.isValid() && birthday.add(1, 'm').toDate(),
                anniversary:
                  trimRecipient.anniversary &&
                  anniversary.isValid() &&
                  anniversary.add(1, 'm').toDate()
              };
            }),
            [a => !a.hasError]
          )
        ]);

        resolve();
      };
    });
  };

  const checkCustomErrors = async ({ hasErrorRecipients, uploadNextButtonClick }) => {
    let hasCustomError = false;

    if (innerStep === INNER_STEP.table) {
      if (hasErrorRecipients?.length) {
        await addModalError(
          <Trans
            i18nKey={VALIDATION_ERRORS.missingContactInformation}
            components={{ strong: <strong /> }}
          />
        );
        hasCustomError = true;
      } else {
        await reset();
      }
    }
    if (innerStep === INNER_STEP.upload && !uploadedRecipients?.length && uploadNextButtonClick) {
      await addModalError(
        <Trans i18nKey={VALIDATION_ERRORS.uploadSpreadsheet} components={{ strong: <strong /> }} />
      );
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const onUploadNextButtonClick = async () => {
    const hasError = await checkCustomErrors({ uploadNextButtonClick: true });

    if (!hasError) {
      setInnerStep(INNER_STEP.table);
    }
  };

  const hasErrorRecipients = useMemo(() => {
    return uploadedRecipients.filter(r => r.hasError);
  }, [uploadedRecipients]);

  useEffect(() => {
    checkCustomErrors({ hasErrorRecipients });
  }, [hasErrorRecipients, innerStep]);

  useEffect(() => {
    if (innerStep === INNER_STEP.upload) {
      setFooterProps({
        leftButtonText: t('cancel'),
        leftButtonOnClick: () => setActiveScene(STEP.select),
        isLeftButtonActive: true,
        isRightButtonActive: true,
        rightButtonOnClick: onUploadNextButtonClick,
        rightButtonText: t('next'),
        rightButtonEndIcon: <Icon name="next" />
      });
    } else if (innerStep === INNER_STEP.table) {
      setFooterProps({
        leftButtonText: t('cancel'),
        leftButtonOnClick: () => setActiveScene(STEP.select),
        isLeftButtonActive: true,
        isRightButtonActive: uploadedRecipients.length > 0 && hasErrorRecipients.length === 0,
        rightButtonOnClick: () => setStep(REVIEW_IMPORT_STEP),
        rightButtonText: t('next'),
        rightButtonEndIcon: <Icon name="next" />
      });
    }
  }, [uploadedRecipients, innerStep]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return innerStep === INNER_STEP.upload ? (
    <FileUploadArea
      handleFile={handleFile}
      status={uploadedRecipients?.length && 'success'}
      successButton={{
        onClick: () => {
          setUploadedRecipients([]);
        },
        text: t('scanningSuccessful')
      }}
    />
  ) : (
    innerStep === INNER_STEP.table && (
      <>
        <S.TitleHeader>
          <Typography variant="body1Bold">
            {hasErrorRecipients.length ? (
              <S.IconWrapper>
                <Icon name="f-error" color="var(--color-error-default)" />
                <Trans
                  i18nKey="uploadSpreadSheetError"
                  values={{
                    errorContactCount: hasErrorRecipients.length,
                    type: t('contact').toLocaleLowerCase()
                  }}
                  components={{ b: <S.ErrorText /> }}
                />
              </S.IconWrapper>
            ) : (
              <S.IconWrapper>
                <S.SuccessIconWrapper>
                  <Icon name="success" />
                </S.SuccessIconWrapper>
                {t('uploadSpreadSheetSuccess', {
                  totalContactCount: uploadedRecipients.length
                })}
              </S.IconWrapper>
            )}
          </Typography>
          {hasErrorRecipients.length > 0 && (
            <Typography variant="body2" color="fg" emphasis="weaker">
              {t('uploadSpreadSheetDescription', { type: t('contacts').toLocaleLowerCase() })}
            </Typography>
          )}
        </S.TitleHeader>

        <SpreadSheetContactTable
          contacts={uploadedRecipients}
          updateContacts={setUploadedRecipients}
          isLoading={isLoading}
          setActiveScene={setActiveScene}
        />
        <S.InfoText>
          <Icon name="tooltip" size="sm" />
          {t('dragAndDropFileInfo')}
        </S.InfoText>
      </>
    )
  );
}

UploadSpreadSheet.propTypes = {
  isLoading: PropTypes.bool,
  setUploadedRecipients: PropTypes.func,
  setActiveScene: PropTypes.func,
  uploadedRecipients: PropTypes.array,
  setFooterProps: PropTypes.func,
  setStep: PropTypes.func
};

export { UploadSpreadSheet };
